import React, { useState, useEffect } from 'react';
import { useSearchParams } from 'react-router-dom';
import EmailSuppliers from './EmailSuppliers';
import {
  Container,
  Typography,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Button,
  Box,
  TextField,
  IconButton,
  Tooltip,
  Alert,
  Snackbar,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  CircularProgress,
  Drawer,
  List,
  ListItem,
  ListItemText,
  ListItemSecondaryAction,
  Tabs,
  Tab,
  Grid,
  Card,
  CardContent,
  Skeleton,
  Stack
} from '@mui/material';
import { ResponsiveLine } from '@nivo/line';
import { ResponsiveBar } from '@nivo/bar';
import { format, eachDayOfInterval, parseISO, startOfWeek } from 'date-fns';
import {
  Assignment as AssignmentIcon,
  Close as CloseIcon,
  Edit as EditIcon,
  Delete as DeleteIcon,
  Add as AddIcon,
  BarChart as BarChartIcon,
  Business as BusinessIcon,
  Visibility as VisibilityIcon,

  Email as EmailIcon,
  Download as DownloadIcon,

} from '@mui/icons-material';
import jsPDF from 'jspdf';
import html2canvas from 'html2canvas';

import { 
  getTags,
  createTag,
  updateTag,
  deleteTag,
  Tag
} from '../../services/tagService';
import LoadingState from '../../components/LoadingState';
import PageContainer from '../../components/PageContainer';

import apiService from '../../services/apiService';
import DateRangePicker from '../../components/analytics/DateRangePicker';
import { DateRange, DateRangeType } from '../../services/analyticsService';

interface TabPanelProps {
  children?: React.ReactNode;
  index: number;
  value: number;
}

function TabPanel(props: TabPanelProps) {
  const { children, value, index, ...other } = props;

  return (
    <div
      role="tabpanel"
      hidden={value !== index}
      id={`tag-tabpanel-${index}`}
      aria-labelledby={`tag-tab-${index}`}
      {...other}
    >
      {value === index && <Box sx={{ p: 3 }}>{children}</Box>}
    </div>
  );
}

  interface AgencyData {
    unique_opens?: number;
    total_opens?: number;
    unique_clicks?: number;
    total_clicks?: number;
  }

  interface DailyOpensData {
    date: string;
    unique_opens: number;
    total_opens: number;
    agencies: Record<string, AgencyData>;
  }

  interface DailyClicksData {
    date: string;
    unique_clicks: number;
    total_clicks: number;
    agencies: Record<string, AgencyData>;
  }

  interface TagAnalytics {
  tag_id: number;
  tag_name: string;
  total_emails: number;
  total_sent: number;
  total_unique_opens: number;
  total_opens: number;
  total_unique_clicks: number;
  total_clicks: number;
  unique_open_rate: number;
  total_open_rate: number;
  unique_click_rate: number;
  total_click_rate: number;
  daily_opens: DailyOpensData[];
  daily_clicks: DailyClicksData[];
  emails: EmailAnalytics[];
  agency_performance: AgencyPerformance[];
}

interface EmailAnalytics {
  name: string;
  subject: string;
  scheduled_date: string | null;
}

interface AgencyPerformance {
  client_id: number;
  client_name: string;
  sent: number;
  unique_opens: number;
  total_opens: number;
  unique_clicks: number;
  total_clicks: number;
  unique_open_rate: number;
  total_open_rate: number;
  unique_click_rate: number;
  total_click_rate: number;
}

const Tags: React.FC = () => {
  const [searchParams, setSearchParams] = useSearchParams();
  const [loading, setLoading] = useState(false);
  const [tags, setTags] = useState<Tag[]>([]);
  const [selectedTab, setSelectedTab] = useState(0); // 0: Email Tags, 1: Analytics
  const [snackbar, setSnackbar] = useState<{ open: boolean; message: string; severity: 'success' | 'error' }>({
    open: false,
    message: '',
    severity: 'success'
  });
  
  // Tag management state
  const [tagDrawerOpen, setTagDrawerOpen] = useState(false);
  const [newTagName, setNewTagName] = useState('');
  const [editingTag, setEditingTag] = useState<Tag | null>(null);
  const [editTagName, setEditTagName] = useState('');
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [tagToDelete, setTagToDelete] = useState<Tag | null>(null);
  
  // Analytics state
  const [selectedTagId, setSelectedTagId] = useState<number | ''>('');
  const [tagAnalytics, setTagAnalytics] = useState<TagAnalytics | null>(null);
  const [loadingAnalytics, setLoadingAnalytics] = useState(false);

  // Date range state for both tabs
  const [dateRange, setDateRange] = useState<DateRange>(() => {
    const end = new Date();
    const start = new Date();
    start.setDate(start.getDate() - 30);  // last 30 days
    return {
      date_range: 'last_30_days',
      startDate: start,
      endDate: end
    };
  });

  const handleDateRangeChange = (newDateRange: DateRange) => {
    // For custom date ranges, use the provided dates
    if (newDateRange.date_range === 'custom') {
      setDateRange(newDateRange);
      // Update URL parameters for custom date range
      const newParams = new URLSearchParams(searchParams);
      newParams.set('date_range', 'custom');
      if (newDateRange.startDate) {
        newParams.set('start_date', newDateRange.startDate.toISOString());
      }
      if (newDateRange.endDate) {
        newParams.set('end_date', newDateRange.endDate.toISOString());
      }
      setSearchParams(newParams);
      return;
    }

    // Calculate dates based on range type
    const end = new Date();
    const start = new Date();

    switch (newDateRange.date_range) {
      case 'last_7_days':
        start.setDate(end.getDate() - 7);
        break;
      case 'last_30_days':
        start.setDate(end.getDate() - 30);
        break;
      case 'last_90_days':
        start.setDate(end.getDate() - 90);
        break;
      case 'last_12_months':
        start.setMonth(end.getMonth() - 12);
        break;
      default:
        start.setDate(end.getDate() - 30); // default to last 30 days
    }

    // Set time to start of day for start date and end of day for end date
    start.setHours(0, 0, 0, 0);
    end.setHours(23, 59, 59, 999);

    setDateRange({
      date_range: newDateRange.date_range,
      startDate: start,
      endDate: end
    });

    // Update URL parameters
    const newParams = new URLSearchParams(searchParams);
    newParams.set('date_range', newDateRange.date_range);
    // Remove custom date parameters if switching to preset range
    newParams.delete('start_date');
    newParams.delete('end_date');
    setSearchParams(newParams);
  };

  useEffect(() => {
    console.log('[Tags] Component mounted, fetching tags');
    fetchTags();
  }, []);

  // Handle URL parameters for tag analytics
  useEffect(() => {
          console.log('[Tags] URL parameters changed:', {
      searchParams: Object.fromEntries(searchParams.entries()),
      pathname: window.location.pathname,
      href: window.location.href
    });
    
    const tagIdParam = searchParams.get('tag');
    const tabParam = searchParams.get('tab');
    const clientIdParam = searchParams.get('client_id');
    const dateRangeParam = searchParams.get('date_range');
    const startDateParam = searchParams.get('start_date');
    const endDateParam = searchParams.get('end_date');
    
    // Set tab based on parameter or default to manage
    if (tabParam) {
      switch (tabParam) {
        case 'assign':
          setSelectedTab(0);
          break;
        case 'analytics':
          setSelectedTab(1);
          break;
        case 'debug':
          setSelectedTab(1); // Redirect debug to analytics since debug tab was removed
          break;
        default:
          setSelectedTab(0); // Default to manage
      }
    } else {
      setSelectedTab(0); // Default to manage
    }
    
    // Handle date range parameters
    if (dateRangeParam) {
      if (dateRangeParam === 'custom' && startDateParam && endDateParam) {
        // Custom date range
        setDateRange({
          date_range: 'custom',
          startDate: new Date(startDateParam),
          endDate: new Date(endDateParam)
        });
      } else {
        // Preset date range
        const end = new Date();
        const start = new Date();

        switch (dateRangeParam) {
          case 'last_7_days':
            start.setDate(end.getDate() - 7);
            break;
          case 'last_30_days':
            start.setDate(end.getDate() - 30);
            break;
          case 'last_90_days':
            start.setDate(end.getDate() - 90);
            break;
          case 'last_12_months':
            start.setMonth(end.getMonth() - 12);
            break;
          default:
            start.setDate(end.getDate() - 30); // default to last 30 days
        }

        // Set time to start of day for start date and end of day for end date
        start.setHours(0, 0, 0, 0);
        end.setHours(23, 59, 59, 999);

        setDateRange({
          date_range: dateRangeParam as DateRangeType,
          startDate: start,
          endDate: end
        });
      }
    }
    
    // Only process tag parameter if on analytics or debug tab or if tag param exists (for deep linking)
    if (tagIdParam) {
      const tagId = parseInt(tagIdParam, 10);
      if (!isNaN(tagId)) {
        setSelectedTagId(tagId);
        
        // If we have a tag parameter, ensure we're on analytics tab
        if (tabParam !== 'analytics') {
          setSelectedTab(1); // Default to analytics tab
        }
      }
    } else {
      // Clear tag selection if no tag parameter
      setSelectedTagId('');
    }
  }, [searchParams]);
  
  // Additional deep link handling - check URL on mount
  useEffect(() => {
    console.log('[Tags] Component mounted, checking for deep link parameters');
    
    // Check if we have URL parameters that weren't processed by searchParams
    const urlParams = new URLSearchParams(window.location.search);
    const tagParam = urlParams.get('tag');
    const tabParam = urlParams.get('tab');
    const clientIdParam = urlParams.get('client_id');
    
    console.log('[Tags] Deep link check:', {
      tagParam,
      tabParam,
      clientIdParam,
      currentSearchParams: Object.fromEntries(searchParams.entries())
    });
    
    // If we have tag parameter but it's not in searchParams, process it
    if (tagParam && !searchParams.get('tag')) {
      console.log('[Tags] Processing tag parameter from URL:', tagParam);
      const tagId = parseInt(tagParam, 10);
      if (!isNaN(tagId)) {
        setSelectedTagId(tagId);
        console.log('[Tags] Set selected tag ID:', tagId);
      }
    }
    
    // If we have tab parameter but it's not in searchParams, process it
    if (tabParam && !searchParams.get('tab')) {
              console.log('[Tags] Processing tab parameter from URL:', tabParam);
        switch (tabParam) {
                  case 'debug':
          setSelectedTab(1); // Redirect debug to analytics since debug tab was removed
          console.log('[Tags] Set selected tab to analytics (1) - debug tab was removed');
          break;
          case 'analytics':
            setSelectedTab(1);
            console.log('[Tags] Set selected tab to analytics (1)');
            break;
          case 'assign':
            setSelectedTab(0);
            console.log('[Tags] Set selected tab to analytics (0)');
            break;
        }
    }
    
    // Client ID is now handled by UserContext, so we don't need to process it here
    if (clientIdParam) {
      console.log('[Tags] Client ID parameter detected:', clientIdParam, '- handled by UserContext');
    }
  }, []); // Only run on mount

  useEffect(() => {
    if (selectedTagId && selectedTab === 1) { // Only analytics tab (index 1)
      fetchTagAnalytics(selectedTagId as number);
    }
  }, [selectedTagId, selectedTab, dateRange]);

  const fetchTags = async () => {
    try {
      setLoading(true);
      const tagsData = await getTags();
      setTags(tagsData);
    } catch (error) {
      console.error('Error fetching tags:', error);
      showSnackbar('Error loading tags', 'error');
    } finally {
      setLoading(false);
    }
  };

  const fetchTagAnalytics = async (tagId: number) => {
    try {
      setLoadingAnalytics(true);
      
      // Build query parameters for date range
      const params = new URLSearchParams();
      params.append('date_range', dateRange.date_range);
      
      if (dateRange.date_range === 'custom' && dateRange.startDate && dateRange.endDate) {
        params.append('start_date', dateRange.startDate.toISOString());
        params.append('end_date', dateRange.endDate.toISOString());
      }
      
      const response = await apiService.get<TagAnalytics>(`/api/tags/${tagId}/analytics?${params.toString()}`);
      setTagAnalytics(response.data);
    } catch (error) {
      console.error('Error fetching tag analytics:', error);
      showSnackbar('Error loading tag analytics', 'error');
    } finally {
      setLoadingAnalytics(false);
    }
  };

  const showSnackbar = (message: string, severity: 'success' | 'error') => {
    setSnackbar({ open: true, message, severity });
  };

  const handleTabChange = (event: React.SyntheticEvent, newValue: number) => {
    setSelectedTab(newValue);
    
    // Update URL parameters to include the tab
    const newParams = new URLSearchParams(searchParams);
    const tabNames = ['assign', 'analytics'];
    newParams.set('tab', tabNames[newValue]);
    
    // Remove tag parameter if switching to non-analytics/debug tabs
    if (newValue !== 1 && newValue !== 2) { // 1 is analytics, 2 is debug tab index
      newParams.delete('tag');
      setSelectedTagId(''); // Clear the selected tag
    }
    
    setSearchParams(newParams);
  };

  const handleTagSelection = (tagId: number | '') => {
    setSelectedTagId(tagId);
    
    // Update URL parameters
    if (tagId !== '') {
      const tabNames = ['assign', 'analytics'];
      const newParams = new URLSearchParams(searchParams);
      newParams.set('tab', tabNames[selectedTab]);
      
      // Only add tag parameter if on analytics tab
      if (selectedTab === 1) { // 1 is analytics tab index
        newParams.set('tag', tagId.toString());
      }
      
      setSearchParams(newParams);
    } else {
      // Remove tag parameter if no tag is selected
      const newParams = new URLSearchParams(searchParams);
      newParams.delete('tag');
      setSearchParams(newParams);
    }
  };
  
  // Function to handle client selection and update URL
  const handleClientSelection = (clientId: number | '') => {
    // This is now handled by TopBar, but we can add any local logic here if needed
    console.log('[Tags] Client selection changed to:', clientId);
  };

  // Tag management functions
  const handleCreateTag = async () => {
    if (!newTagName.trim()) return;
    
    try {
      const newTag = await createTag({ name: newTagName.trim() });
      setTags([...tags, newTag]);
      setNewTagName('');
      showSnackbar('Tag created successfully', 'success');
    } catch (error) {
      console.error('Error creating tag:', error);
      showSnackbar('Error creating tag', 'error');
    }
  };

  const handleUpdateTag = async () => {
    if (!editingTag || !editTagName.trim()) return;
    
    try {
      const updatedTag = await updateTag(editingTag.id, { name: editTagName.trim() });
      setTags(tags.map(t => t.id === updatedTag.id ? updatedTag : t));
      setEditingTag(null);
      setEditTagName('');
      showSnackbar('Tag updated successfully', 'success');
    } catch (error) {
      console.error('Error updating tag:', error);
      showSnackbar('Error updating tag', 'error');
    }
  };

  const handleDeleteTag = async () => {
    if (!tagToDelete) return;
    
    try {
      await deleteTag(tagToDelete.id);
      setTags(tags.filter(t => t.id !== tagToDelete.id));
      setDeleteDialogOpen(false);
      setTagToDelete(null);
      showSnackbar('Tag deleted successfully', 'success');
    } catch (error) {
      console.error('Error deleting tag:', error);
      showSnackbar('Error deleting tag', 'error');
    }
  };

  const openEditDialog = (tag: Tag) => {
    setEditingTag(tag);
    setEditTagName(tag.name);
  };

  const openDeleteDialog = (tag: Tag) => {
    setTagToDelete(tag);
    setDeleteDialogOpen(true);
  };

  const getDateRange = (opensData: DailyOpensData[], clicksData: DailyClicksData[]) => {
    const allDates = [
      ...opensData.map(d => new Date(d.date)),
      ...clicksData.map(d => new Date(d.date))
    ].sort((a, b) => a.getTime() - b.getTime());
    
    if (allDates.length === 0) return null;
    
    return {
      earliest: allDates[0],
      latest: allDates[allDates.length - 1]
    };
  };

  const getAggregationLevel = (dateRange: { earliest: Date; latest: Date }) => {
    const daysDiff = Math.ceil((dateRange.latest.getTime() - dateRange.earliest.getTime()) / (1000 * 60 * 60 * 24));
    
    if (daysDiff <= 31) {
      return 'daily';
    } else if (daysDiff <= 90) {
      return 'weekly';
    } else {
      return 'monthly';
    }
  };

  const aggregateDataByPeriod = (data: DailyOpensData[] | DailyClicksData[], aggregationLevel: 'daily' | 'weekly' | 'monthly') => {
    const aggregated = new Map<string, any>();
    
    data.forEach(item => {
      const date = new Date(item.date);
      let periodKey: string;
      
      switch (aggregationLevel) {
        case 'daily':
          periodKey = format(date, 'yyyy-MM-dd');
          break;
        case 'weekly':
          // Get the start of the week (Monday)
          const weekStart = startOfWeek(date, { weekStartsOn: 1 });
          periodKey = format(weekStart, 'yyyy-MM-dd');
          break;
        case 'monthly':
          periodKey = format(date, 'yyyy-MM');
          break;
      }
      
      if (!aggregated.has(periodKey)) {
        aggregated.set(periodKey, {
          unique_opens: 0,
          total_opens: 0,
          unique_clicks: 0,
          total_clicks: 0,
          agencies: {}
        });
      }
      
      const current = aggregated.get(periodKey)!;
      
      // Check if this is opens or clicks data
      if ('unique_opens' in item) {
        current.unique_opens += item.unique_opens || 0;
        current.total_opens += item.total_opens || 0;
        // Merge agencies data
        Object.entries(item.agencies || {}).forEach(([agency, agencyData]) => {
          if (!current.agencies[agency]) {
            current.agencies[agency] = { unique_opens: 0, total_opens: 0, unique_clicks: 0, total_clicks: 0 };
          }
          current.agencies[agency].unique_opens += agencyData.unique_opens || 0;
          current.agencies[agency].total_opens += agencyData.total_opens || 0;
        });
      } else {
        current.unique_clicks += item.unique_clicks || 0;
        current.total_clicks += item.total_clicks || 0;
        // Merge agencies data
        Object.entries(item.agencies || {}).forEach(([agency, agencyData]) => {
          if (!current.agencies[agency]) {
            current.agencies[agency] = { unique_opens: 0, total_opens: 0, unique_clicks: 0, total_clicks: 0 };
          }
          current.agencies[agency].unique_clicks += agencyData.unique_clicks || 0;
          current.agencies[agency].total_clicks += agencyData.total_clicks || 0;
        });
      }
    });
    
    // Convert to array and sort by date chronologically
    return Array.from(aggregated.entries())
      .map(([period, data]) => ({
        date: period,
        ...data
      }))
      .sort((a, b) => {
        // Parse dates for proper chronological sorting
        const dateA = new Date(a.date);
        const dateB = new Date(b.date);
        return dateA.getTime() - dateB.getTime();
      });
  };

  const prepareOpensChartData = (dailyOpens: DailyOpensData[], dateRange: { earliest: Date; latest: Date } | null) => {
    if (!dailyOpens || dailyOpens.length === 0 || !dateRange) return [];

    // Create a map of dates to opens for quick lookup
    const opensMap = new Map(
      dailyOpens.map(day => [
        format(parseISO(day.date), 'yyyy-MM-dd'),
        { 
          unique: day.unique_opens, 
          total: day.total_opens,
          agencies: day.agencies
        }
      ])
    );

    // Create data points for the entire date range
    const allDates = eachDayOfInterval({
      start: dateRange.earliest,
      end: dateRange.latest
    });

    const uniqueOpensData = allDates.map(date => ({
      x: format(date, 'yyyy-MM-dd'),
      y: opensMap.get(format(date, 'yyyy-MM-dd'))?.unique || 0,
      agencies: opensMap.get(format(date, 'yyyy-MM-dd'))?.agencies || {}
    }));

    const totalOpensData = allDates.map(date => ({
      x: format(date, 'yyyy-MM-dd'),
      y: opensMap.get(format(date, 'yyyy-MM-dd'))?.total || 0,
      agencies: opensMap.get(format(date, 'yyyy-MM-dd'))?.agencies || {}
    }));

    return [
      {
        id: 'Unique Opens',
        color: '#1976d2', // primary blue
        data: uniqueOpensData
      },
      {
        id: 'Total Opens',
        color: '#d32f2f', // secondary red
        data: totalOpensData
      }
    ];
  };

  const prepareClicksChartData = (dailyClicks: DailyClicksData[], dateRange: { earliest: Date; latest: Date } | null) => {
    if (!dailyClicks || dailyClicks.length === 0 || !dateRange) return [];

    // Create a map of dates to clicks for quick lookup
    const clicksMap = new Map(
      dailyClicks.map(day => [
        format(parseISO(day.date), 'yyyy-MM-dd'),
        { 
          unique: day.unique_clicks, 
          total: day.total_clicks,
          agencies: day.agencies
        }
      ])
    );

    // Create data points for the entire date range
    const allDates = eachDayOfInterval({
      start: dateRange.earliest,
      end: dateRange.latest
    });

    const uniqueClicksData = allDates.map(date => ({
      x: format(date, 'yyyy-MM-dd'),
      y: clicksMap.get(format(date, 'yyyy-MM-dd'))?.unique || 0,
      agencies: clicksMap.get(format(date, 'yyyy-MM-dd'))?.agencies || {}
    }));

    const totalClicksData = allDates.map(date => ({
      x: format(date, 'yyyy-MM-dd'),
      y: clicksMap.get(format(date, 'yyyy-MM-dd'))?.total || 0,
      agencies: clicksMap.get(format(date, 'yyyy-MM-dd'))?.agencies || {}
    }));

    return [
      {
        id: 'Unique Clicks',
        color: '#1976d2', // primary blue
        data: uniqueClicksData
      },
      {
        id: 'Total Clicks',
        color: '#d32f2f', // secondary red
        data: totalClicksData
      }
    ];
  };

  const createCustomTooltip = (metricType: 'opens' | 'clicks') => {
    return ({ point }: any) => {
      const date = point.data.x;
      const value = point.data.y;
      const agencies = point.data.agencies || {};
      
      const agencyLines = Object.entries(agencies).map(([agency, data]: [string, any]) => {
        const uniqueValue = data[`unique_${metricType}`] || 0;
        const totalValue = data[`total_${metricType}`] || 0;
        return `${agency}: ${uniqueValue} unique, ${totalValue} total`;
      }).join('\n');

      return (
        <Box
          sx={{
            background: 'white',
            border: '1px solid #ccc',
            borderRadius: '4px',
            padding: '8px',
            fontSize: '12px',
            fontFamily: 'monospace',
            whiteSpace: 'pre-line'
          }}
        >
          <strong>Date: {date}</strong>
          <br />
          <strong>{point.serieId}: {value}</strong>
          {agencyLines && (
            <>
              <br />
              <strong>Agencies:</strong>
              <br />
              {agencyLines}
            </>
          )}
        </Box>
      );
    };
  };

  const exportToPDF = async () => {
    if (!tagAnalytics) return;
    
    const pdf = new jsPDF('p', 'mm', 'a4');
    const pageWidth = pdf.internal.pageSize.getWidth();
    const pageHeight = pdf.internal.pageSize.getHeight();
    const margin = 15;
    const contentWidth = pageWidth - (margin * 2);
    
    let yPosition = margin;
    
    // Title
    pdf.setFontSize(20);
    pdf.setFont('helvetica', 'bold');
    pdf.setTextColor(25, 118, 210); // Blue
    pdf.text(`Tag Analytics Report`, pageWidth / 2, yPosition, { align: 'center' });
    yPosition += 10;
    
    // Date
    pdf.setFontSize(12);
    pdf.setFont('helvetica', 'normal');
    pdf.setTextColor(100);
    pdf.text(`Generated: ${new Date().toLocaleDateString()}`, pageWidth / 2, yPosition, { align: 'center' });
    yPosition += 10;
    
    // Tag name
    pdf.setFontSize(16);
    pdf.setFont('helvetica', 'bold');
    pdf.setTextColor(0);
    pdf.text(`Tag: ${tagAnalytics.tag_name}`, margin, yPosition);
    yPosition += 20;
    
    // Summary Cards (styled like the page)
    pdf.setFontSize(14);
    pdf.setTextColor(25, 118, 210);
    pdf.text('Summary Metrics', margin, yPosition);
    yPosition += 15;
    
    // Card dimensions and colors
    const cardWidth = (contentWidth - 20) / 5; // 5 cards per row
    const cardHeight = 25;
    const cardSpacing = 5;
    
    const cardColors = {
      totalSent: [25, 118, 210], // Blue
      totalOpens: [255, 152, 0], // Orange
      uniqueOpens: [255, 193, 7], // Yellow
      totalClicks: [255, 152, 0], // Orange
      uniqueClicks: [255, 193, 7] // Yellow
    };
    
    // Create cards with null checks
    const cards = [
      {
        title: 'Total Sent',
        value: (tagAnalytics.total_sent || 0).toLocaleString(),
        subtitle: `${(tagAnalytics.total_emails || 0).toLocaleString()} clients`,
        color: cardColors.totalSent
      },
      {
        title: 'Total Opens',
        value: (tagAnalytics.total_opens || 0).toLocaleString(),
        subtitle: `(${(tagAnalytics.total_open_rate || 0).toFixed(1)}%)`,
        color: cardColors.totalOpens
      },
      {
        title: 'Unique Opens',
        value: (tagAnalytics.total_unique_opens || 0).toLocaleString(),
        subtitle: `(${(tagAnalytics.unique_open_rate || 0).toFixed(1)}%)`,
        color: cardColors.uniqueOpens
      },
      {
        title: 'Total Clicks',
        value: (tagAnalytics.total_clicks || 0).toLocaleString(),
        subtitle: `(${(tagAnalytics.total_click_rate || 0).toFixed(1)}%)`,
        color: cardColors.totalClicks
      },
      {
        title: 'Unique Clicks',
        value: (tagAnalytics.total_unique_clicks || 0).toLocaleString(),
        subtitle: `(${(tagAnalytics.unique_click_rate || 0).toFixed(1)}%)`,
        color: cardColors.uniqueClicks
      }
    ];
    
    // Draw cards in a row
    cards.forEach((card, index) => {
      const xPos = margin + (index * (cardWidth + cardSpacing));
      
      // Card background
      pdf.setFillColor(248, 249, 250);
      pdf.rect(xPos, yPosition, cardWidth, cardHeight, 'F');
      pdf.setDrawColor(200, 200, 200);
      pdf.rect(xPos, yPosition, cardWidth, cardHeight, 'S');
      
      // Card title
      pdf.setFontSize(8);
      pdf.setFont('helvetica', 'normal');
      pdf.setTextColor(100);
      pdf.text(card.title, xPos + (cardWidth / 2), yPosition + 5, { align: 'center' });
      
      // Card value
      pdf.setFontSize(12);
      pdf.setFont('helvetica', 'bold');
      pdf.setTextColor(card.color[0], card.color[1], card.color[2]);
      pdf.text(card.value, xPos + (cardWidth / 2), yPosition + 12, { align: 'center' });
      
      // Card subtitle
      pdf.setFontSize(7);
      pdf.setFont('helvetica', 'normal');
      pdf.setTextColor(100);
      pdf.text(card.subtitle, xPos + (cardWidth / 2), yPosition + 18, { align: 'center' });
    });
    
    yPosition += cardHeight + 20;
    
    // Emails Table
    pdf.setFontSize(14);
    pdf.setTextColor(25, 118, 210);
    pdf.text('Emails', margin, yPosition);
    yPosition += 10;
    
    // Table styling
    const emailTableHeaders = ['Email Name', 'Subject', 'Scheduled Date'];
    const emailColWidths = [60, 80, 40];
    
    // Table header
    let xPos = margin;
    pdf.setFillColor(240, 240, 240);
    pdf.rect(margin, yPosition - 5, contentWidth, 8, 'F');
    
    pdf.setFontSize(8);
    pdf.setFont('helvetica', 'bold');
    pdf.setTextColor(0);
    emailTableHeaders.forEach((header, index) => {
      pdf.text(header, xPos + 2, yPosition);
      xPos += emailColWidths[index];
    });
    
    yPosition += 10;
    
    // Table data
    pdf.setFont('helvetica', 'normal');
    tagAnalytics.emails.forEach((email) => {
      // Check if we need a new page
      if (yPosition > pageHeight - 30) {
        pdf.addPage();
        yPosition = 20;
      }
      
      xPos = margin;
      pdf.setTextColor(0);
      pdf.setFontSize(7);
      
      // Truncate long text to fit columns
      const emailName = email.name.length > 35 ? email.name.substring(0, 32) + '...' : email.name;
      const subject = email.subject.length > 45 ? email.subject.substring(0, 42) + '...' : email.subject;
      const scheduledDate = email.scheduled_date ? new Date(email.scheduled_date).toLocaleDateString() : 'Not scheduled';
      
      pdf.text(emailName, xPos, yPosition);
      xPos += emailColWidths[0];
      pdf.text(subject, xPos, yPosition);
      xPos += emailColWidths[1];
      pdf.text(scheduledDate, xPos, yPosition);
      
      yPosition += 5;
    });
    
    yPosition += 10;
    
    // Agency Performance Table
    pdf.setFontSize(14);
    pdf.setTextColor(25, 118, 210);
    pdf.text('Agency Performance', margin, yPosition);
    yPosition += 10;
    
    // Table styling
    const agencyTableHeaders = ['Agency', 'Emails Sent', 'Total Opens', 'Unique Opens', 'Total Clicks', 'Unique Clicks'];
    const agencyColWidths = [40, 25, 25, 25, 25, 25];
    
    // Table header
    xPos = margin;
    pdf.setFillColor(240, 240, 240);
    pdf.rect(margin, yPosition - 5, contentWidth, 8, 'F');
    
    pdf.setFontSize(8);
    pdf.setFont('helvetica', 'bold');
    pdf.setTextColor(0);
    agencyTableHeaders.forEach((header, index) => {
      pdf.text(header, xPos + 2, yPosition);
      xPos += agencyColWidths[index];
    });
    
    yPosition += 10;
    
    // Table data
    pdf.setFont('helvetica', 'normal');
    tagAnalytics.agency_performance?.forEach((agency) => {
      // Check if we need a new page
      if (yPosition > pageHeight - 30) {
        pdf.addPage();
        yPosition = 20;
      }
      
      xPos = margin;
      pdf.setTextColor(0);
      pdf.setFontSize(7);
      
      // Truncate long text to fit columns
      const agencyName = agency.client_name.length > 20 ? agency.client_name.substring(0, 17) + '...' : agency.client_name;
      
      pdf.text(agencyName, xPos, yPosition);
      xPos += agencyColWidths[0];
      pdf.text((agency.sent || 0).toLocaleString(), xPos, yPosition);
      xPos += agencyColWidths[1];
      pdf.text(`${(agency.total_opens || 0).toLocaleString()} (${(agency.total_open_rate || 0).toFixed(1)}%)`, xPos, yPosition);
      xPos += agencyColWidths[2];
      pdf.text(`${(agency.unique_opens || 0).toLocaleString()} (${(agency.unique_open_rate || 0).toFixed(1)}%)`, xPos, yPosition);
      xPos += agencyColWidths[3];
      pdf.text(`${(agency.total_clicks || 0).toLocaleString()} (${(agency.total_click_rate || 0).toFixed(1)}%)`, xPos, yPosition);
      xPos += agencyColWidths[4];
      pdf.text(`${(agency.unique_clicks || 0).toLocaleString()} (${(agency.unique_click_rate || 0).toFixed(1)}%)`, xPos, yPosition);
      
      yPosition += 5;
    });
    
    pdf.save(`tag-analytics-${tagAnalytics.tag_name.toLowerCase().replace(/\s+/g, '-')}-${new Date().toISOString().split('T')[0]}.pdf`);
  };

  if (loading) {
    return (
      <PageContainer>
        <LoadingState message="Loading tags..." />
      </PageContainer>
    );
  }

  return (
    <PageContainer>
      <Box display="flex" justifyContent="space-between" alignItems="center" sx={{ mb: 3 }}>
        <Typography variant="subtitle1" color="text.secondary">
          Tags
        </Typography>
      </Box>

      <Paper sx={{ width: '100%' }}>
        <Tabs value={selectedTab} onChange={handleTabChange} aria-label="tag tabs">
            <Tab 
              icon={<AssignmentIcon />} 
              iconPosition="start" 
              label="Email Tags" 
              id="tag-tab-0"
              aria-controls="tag-tabpanel-0"
            />
            <Tab 
              icon={<BarChartIcon />} 
              iconPosition="start" 
              label="Analytics" 
              id="tag-tab-1"
              aria-controls="tag-tabpanel-1"
            />
          </Tabs>

        <TabPanel value={selectedTab} index={0}>
          {/* Assign Emails Tab */}
          <EmailSuppliers 
            dateRange={dateRange}
            onDateRangeChange={handleDateRangeChange}
          />
        </TabPanel>

        <TabPanel value={selectedTab} index={1}>
          {/* Analytics Tab */}
          <Box>
            
            <Box display="flex" gap={2} sx={{ mb: 3 }}>
              <FormControl sx={{ width: '50%' }}>
                <InputLabel>Select Tag</InputLabel>
                <Select
                  value={selectedTagId}
                  onChange={(e) => handleTagSelection(e.target.value as number)}
                  label="Select Tag"
                >
                  <MenuItem value="">
                    <em>Select a tag...</em>
                  </MenuItem>
                  {loading ? (
                    <MenuItem disabled>
                      <Stack direction="row" spacing={1} alignItems="center">
                        <Skeleton width={20} height={20} />
                        <Skeleton width={100} height={20} />
                      </Stack>
                    </MenuItem>
                  ) : (
                    tags.map((tag) => (
                      <MenuItem key={tag.id} value={tag.id}>
                        {tag.name}
                      </MenuItem>
                    ))
                  )}
                </Select>
              </FormControl>

              {selectedTagId && (
                <Box sx={{ width: '50%', display: 'flex', justifyContent: 'flex-end' }}>
                  <DateRangePicker 
                    dateRange={dateRange} 
                    onDateRangeChange={handleDateRangeChange} 
                  />
                </Box>
              )}
            </Box>

            {selectedTagId && (
              <>
                {loadingAnalytics ? (
                  <Box display="flex" justifyContent="center" p={3}>
                    <CircularProgress />
                  </Box>
                ) : tagAnalytics ? (
                  <>
                    <Box display="flex" justifyContent="space-between" alignItems="center" mb={3}>
                      <Typography variant="h6">
                        Analytics for {tagAnalytics.tag_name}
                      </Typography>
                      <Button
                        variant="outlined"
                        onClick={exportToPDF}
                        startIcon={<DownloadIcon />}
                        disabled={loadingAnalytics}
                      >
                        Export PDF
                      </Button>
                    </Box>
                    {/* Summary Cards */}
                    <Grid container spacing={3} sx={{ mb: 3 }}>
                      {/* First Row - Emails and Opens */}
                      <Grid item xs={12} sm={6} md={2.4}>
                        <Card sx={{ height: '100%' }}>
                          <CardContent sx={{ textAlign: 'center', height: '100%', display: 'flex', flexDirection: 'column', justifyContent: 'space-between', p: 2 }}>
                            <Box display="flex" alignItems="center" justifyContent="center" mb={1} sx={{ minHeight: '28px' }}>
                              <EmailIcon sx={{ mr: 1, fontSize: '1.2rem', color: '#1976d2' }} />
                              <Typography color="text.secondary" noWrap variant="body2">
                                Total Sent
                              </Typography>
                            </Box>
                            <Typography variant="h4" sx={{ color: '#1976d2' }}>
                              {(tagAnalytics.total_sent || 0).toLocaleString()}
                            </Typography>
                            <Typography variant="body2" color="text.secondary">
                              {(tagAnalytics.total_emails || 0).toLocaleString()} clients
                            </Typography>
                          </CardContent>
                        </Card>
                      </Grid>
                      <Grid item xs={12} sm={6} md={2.4}>
                        <Card sx={{ height: '100%' }}>
                          <CardContent sx={{ textAlign: 'center', height: '100%', display: 'flex', flexDirection: 'column', justifyContent: 'space-between', p: 2 }}>
                            <Box display="flex" alignItems="center" justifyContent="center" mb={1} sx={{ minHeight: '28px' }}>
                              <VisibilityIcon sx={{ mr: 1, fontSize: '1.2rem', color: '#ff9800' }} />
                              <Typography color="text.secondary" noWrap variant="body2">
                                Total Opens
                              </Typography>
                            </Box>
                            <Typography variant="h4" sx={{ color: '#ff9800' }}>
                              {(tagAnalytics.total_opens || 0).toLocaleString()}
                            </Typography>
                            <Typography variant="body2" color="text.secondary">
                              ({(tagAnalytics.total_open_rate || 0).toFixed(1)}%)
                            </Typography>
                          </CardContent>
                        </Card>
                      </Grid>
                      <Grid item xs={12} sm={6} md={2.4}>
                        <Card sx={{ height: '100%' }}>
                          <CardContent sx={{ textAlign: 'center', height: '100%', display: 'flex', flexDirection: 'column', justifyContent: 'space-between', p: 2 }}>
                            <Box display="flex" alignItems="center" justifyContent="center" mb={1} sx={{ minHeight: '28px' }}>
                              <VisibilityIcon sx={{ mr: 1, fontSize: '1.2rem', color: '#ffc107' }} />
                              <Typography color="text.secondary" noWrap variant="body2">
                                Unique Opens
                              </Typography>
                            </Box>
                            <Typography variant="h4" sx={{ color: '#ffc107' }}>
                              {(tagAnalytics.total_unique_opens || 0).toLocaleString()}
                            </Typography>
                            <Typography variant="body2" color="text.secondary">
                              ({(tagAnalytics.unique_open_rate || 0).toFixed(1)}%)
                            </Typography>
                          </CardContent>
                        </Card>
                      </Grid>
                      
                      {/* Second Row - Clicks */}
                      <Grid item xs={12} sm={6} md={2.4}>
                        <Card sx={{ height: '100%' }}>
                          <CardContent sx={{ textAlign: 'center', height: '100%', display: 'flex', flexDirection: 'column', justifyContent: 'space-between', p: 2 }}>
                            <Box display="flex" alignItems="center" justifyContent="center" mb={1} sx={{ minHeight: '28px' }}>
              
                              <Typography color="text.secondary" noWrap variant="body2">
                                Total Clicks
                              </Typography>
                            </Box>
                            <Typography variant="h4" sx={{ color: '#ff9800' }}>
                              {(tagAnalytics.total_clicks || 0).toLocaleString()}
                            </Typography>
                            <Typography variant="body2" color="text.secondary">
                              ({(tagAnalytics.total_click_rate || 0).toFixed(1)}%)
                            </Typography>
                          </CardContent>
                        </Card>
                      </Grid>
                      <Grid item xs={12} sm={6} md={2.4}>
                        <Card sx={{ height: '100%' }}>
                          <CardContent sx={{ textAlign: 'center', height: '100%', display: 'flex', flexDirection: 'column', justifyContent: 'space-between', p: 2 }}>
                            <Box display="flex" alignItems="center" justifyContent="center" mb={1} sx={{ minHeight: '28px' }}>
              
                              <Typography color="text.secondary" noWrap variant="body2">
                                Unique Clicks
                              </Typography>
                            </Box>
                            <Typography variant="h4" sx={{ color: '#ffc107' }}>
                              {(tagAnalytics.total_unique_clicks || 0).toLocaleString()}
                            </Typography>
                            <Typography variant="body2" color="text.secondary">
                              ({(tagAnalytics.unique_click_rate || 0).toFixed(1)}%)
                            </Typography>
                          </CardContent>
                        </Card>
                      </Grid>
                    </Grid>

                    {/* Graphs Side by Side */}
                                        {(tagAnalytics.daily_opens?.length > 0 || tagAnalytics.daily_clicks?.length > 0) && (
                      <Paper sx={{ p: 3, mb: 3 }}>
                        <Grid container spacing={3}>
                          {/* Opens Graph */}
                          {tagAnalytics.daily_opens && tagAnalytics.daily_opens.length > 0 && (
                            <Grid item xs={12} md={6}>
                              <Typography variant="subtitle1" sx={{ mb: 1 }}>
                                Opens Over Time
                              </Typography>
                              <Box sx={{ height: 350 }}>
                                <ResponsiveBar
                                  data={(() => {
                                    const dateRange = getDateRange(tagAnalytics.daily_opens, tagAnalytics.daily_clicks);
                                    if (!dateRange) return [];
                                    
                                    const aggregationLevel = getAggregationLevel(dateRange);
                                    const aggregatedOpens = aggregateDataByPeriod(tagAnalytics.daily_opens, aggregationLevel);
                                    
                                    return aggregatedOpens.map(item => ({
                                      date: item.date,
                                      'Total Opens': item.total_opens,
                                      'Unique Opens': item.unique_opens
                                    }));
                                  })()}
                                  keys={['Total Opens', 'Unique Opens']}
                                  indexBy="date"
                                  margin={{ top: 60, right: 30, bottom: 80, left: 80 }}
                                  padding={0.3}
                                  groupMode="grouped"
                                  valueScale={{ type: 'linear' }}
                                  indexScale={{ type: 'band', round: true }}
                                  colors={{ scheme: 'nivo' }}
                                  borderColor={{ from: 'color', modifiers: [['darker', 1.6]] }}
                                  axisTop={null}
                                  axisRight={null}
                                  axisBottom={{
                                    tickSize: 5,
                                    tickPadding: 5,
                                    tickRotation: -45,
                                    legend: 'Date',
                                    legendOffset: 50,
                                    legendPosition: 'middle',
                                    truncateTickAt: 0,
                                    format: (value) => {
                                      const dateRange = getDateRange(tagAnalytics.daily_opens, tagAnalytics.daily_clicks);
                                      if (!dateRange) return value;
                                      
                                      const aggregationLevel = getAggregationLevel(dateRange);
                                      const date = new Date(value);
                                      
                                      switch (aggregationLevel) {
                                        case 'daily':
                                          return format(date, 'MMM dd');
                                        case 'weekly':
                                          return format(date, 'MMM dd');
                                        case 'monthly':
                                          return format(date, 'MMM yyyy');
                                        default:
                                          return value;
                                      }
                                    }
                                  }}
                                  axisLeft={{
                                    tickSize: 5,
                                    tickPadding: 5,
                                    tickRotation: 0,
                                    legend: 'Opens',
                                    legendOffset: -60,
                                    legendPosition: 'middle',
                                    format: (value) => `${value.toLocaleString()}`
                                  }}
                                  labelSkipWidth={12}
                                  labelSkipHeight={12}
                                  labelTextColor={{ from: 'color', modifiers: [['darker', 1.6]] }}
                                  legends={[
                                    {
                                      dataFrom: 'keys',
                                      anchor: 'top-right',
                                      direction: 'row',
                                      justify: false,
                                      translateX: 0,
                                      translateY: -30,
                                      itemsSpacing: 20,
                                      itemDirection: 'left-to-right',
                                      itemWidth: 80,
                                      itemHeight: 20,
                                      itemOpacity: 0.75,
                                      symbolSize: 8,
                                      symbolShape: 'circle',
                                      symbolBorderColor: 'rgba(0, 0, 0, .5)',
                                      effects: [
                                        {
                                          on: 'hover',
                                          style: {
                                            itemBackground: 'rgba(0, 0, 0, .03)',
                                            itemOpacity: 1
                                          }
                                        }
                                      ]
                                    }
                                  ]}
                                  tooltip={({ id, value, color }) => (
                                    <Box
                                      sx={{
                                        background: 'white',
                                        padding: '8px',
                                        border: '1px solid #ccc',
                                        borderRadius: '4px'
                                      }}
                                    >
                                      <strong>{id}:</strong> {value.toLocaleString()}
                                    </Box>
                                  )}
                                  theme={{
                                    axis: {
                                      domain: {
                                        line: {
                                          stroke: '#777777',
                                          strokeWidth: 1
                                        }
                                      },
                                      legend: {
                                        text: {
                                          fontSize: 11,
                                          fill: '#333333'
                                        }
                                      },
                                      ticks: {
                                        line: {
                                          stroke: '#777777',
                                          strokeWidth: 1
                                        },
                                        text: {
                                          fontSize: 10,
                                          fill: '#333333'
                                        }
                                      }
                                    },
                                    grid: {
                                      line: {
                                        stroke: '#dddddd',
                                        strokeWidth: 1
                                      }
                                    },
                                    legends: {
                                      text: {
                                        fontSize: 10,
                                        fill: '#333333'
                                      }
                                    }
                                  }}
                                />
                              </Box>
                            </Grid>
                          )}

                          {/* Clicks Graph */}
                          {tagAnalytics.daily_clicks && tagAnalytics.daily_clicks.length > 0 && (
                            <Grid item xs={12} md={6}>
                              <Typography variant="subtitle1" sx={{ mb: 1 }}>
                                Clicks Over Time
                              </Typography>
                              <Box sx={{ height: 350 }}>
                                <ResponsiveBar
                                  data={(() => {
                                    const dateRange = getDateRange(tagAnalytics.daily_opens, tagAnalytics.daily_clicks);
                                    if (!dateRange) return [];
                                    
                                    const aggregationLevel = getAggregationLevel(dateRange);
                                    const aggregatedClicks = aggregateDataByPeriod(tagAnalytics.daily_clicks, aggregationLevel);
                                    
                                    return aggregatedClicks.map(item => ({
                                      date: item.date,
                                      'Total Clicks': item.total_clicks,
                                      'Unique Clicks': item.unique_clicks
                                    }));
                                  })()}
                                  keys={['Total Clicks', 'Unique Clicks']}
                                  indexBy="date"
                                  margin={{ top: 60, right: 30, bottom: 80, left: 80 }}
                                  padding={0.3}
                                  groupMode="grouped"
                                  valueScale={{ type: 'linear' }}
                                  indexScale={{ type: 'band', round: true }}
                                  colors={{ scheme: 'nivo' }}
                                  borderColor={{ from: 'color', modifiers: [['darker', 1.6]] }}
                                  axisTop={null}
                                  axisRight={null}
                                  axisBottom={{
                                    tickSize: 5,
                                    tickPadding: 5,
                                    tickRotation: -45,
                                    legend: 'Date',
                                    legendOffset: 50,
                                    legendPosition: 'middle',
                                    truncateTickAt: 0,
                                    format: (value) => {
                                      const dateRange = getDateRange(tagAnalytics.daily_opens, tagAnalytics.daily_clicks);
                                      if (!dateRange) return value;
                                      
                                      const aggregationLevel = getAggregationLevel(dateRange);
                                      const date = new Date(value);
                                      
                                      switch (aggregationLevel) {
                                        case 'daily':
                                          return format(date, 'MMM dd');
                                        case 'weekly':
                                          return format(date, 'MMM dd');
                                        case 'monthly':
                                          return format(date, 'MMM yyyy');
                                        default:
                                          return value;
                                      }
                                    }
                                  }}
                                  axisLeft={{
                                    tickSize: 5,
                                    tickPadding: 5,
                                    tickRotation: 0,
                                    legend: 'Clicks',
                                    legendOffset: -60,
                                    legendPosition: 'middle',
                                    format: (value) => `${value.toLocaleString()}`
                                  }}
                                  labelSkipWidth={0}
                                  labelSkipHeight={0}
                                  enableLabel={false}
                                  legends={[
                                    {
                                      dataFrom: 'keys',
                                      anchor: 'top-right',
                                      direction: 'row',
                                      justify: false,
                                      translateX: 0,
                                      translateY: -30,
                                      itemsSpacing: 20,
                                      itemDirection: 'left-to-right',
                                      itemWidth: 80,
                                      itemHeight: 20,
                                      itemOpacity: 0.75,
                                      symbolSize: 8,
                                      symbolShape: 'circle',
                                      symbolBorderColor: 'rgba(0, 0, 0, .5)',
                                      effects: [
                                        {
                                          on: 'hover',
                                          style: {
                                            itemBackground: 'rgba(0, 0, 0, .03)',
                                            itemOpacity: 1
                                          }
                                        }
                                      ]
                                    }
                                  ]}
                                  tooltip={({ id, value, color }) => (
                                    <Box
                                      sx={{
                                        background: 'white',
                                        padding: '8px',
                                        border: '1px solid #ccc',
                                        borderRadius: '4px'
                                      }}
                                    >
                                      <strong>{id}:</strong> {value.toLocaleString()}
                                    </Box>
                                  )}
                                  theme={{
                                    axis: {
                                      domain: {
                                        line: {
                                          stroke: '#777777',
                                          strokeWidth: 1
                                        }
                                      },
                                      legend: {
                                        text: {
                                          fontSize: 11,
                                          fill: '#333333'
                                        }
                                      },
                                      ticks: {
                                        line: {
                                          stroke: '#777777',
                                          strokeWidth: 1
                                        },
                                        text: {
                                          fontSize: 10,
                                          fill: '#333333'
                                        }
                                      }
                                    },
                                    grid: {
                                      line: {
                                        stroke: '#dddddd',
                                        strokeWidth: 1
                                      }
                                    },
                                    legends: {
                                      text: {
                                        fontSize: 10,
                                        fill: '#333333'
                                      }
                                    }
                                  }}
                                />
                              </Box>
                            </Grid>
                          )}
                        </Grid>
                      </Paper>
                    )}

                    {/* Emails Table */}
                    <Paper sx={{ p: 3, mb: 3 }}>
                      <Typography variant="h6" gutterBottom>
                        Emails
                      </Typography>
                      <TableContainer>
                        <Table>
                          <TableHead>
                            <TableRow>
                              <TableCell>Email Name</TableCell>
                              <TableCell>Subject</TableCell>
                              <TableCell>Scheduled Date</TableCell>
                            </TableRow>
                          </TableHead>
                          <TableBody>
                            {tagAnalytics.emails.map((email, index) => (
                              <TableRow key={`${email.name}-${email.subject}-${email.scheduled_date}-${index}`}>
                                <TableCell>{email.name}</TableCell>
                                <TableCell>{email.subject}</TableCell>
                                <TableCell>
                                  {email.scheduled_date ? new Date(email.scheduled_date).toLocaleDateString() : 'Not scheduled'}
                                </TableCell>
                              </TableRow>
                            ))}
                          </TableBody>
                        </Table>
                      </TableContainer>
                    </Paper>

                    {/* Agency Performance Table */}
                    <Paper sx={{ p: 3 }}>
                      <Typography variant="h6" gutterBottom>
                        Agency Performance
                      </Typography>
                      <TableContainer>
                        <Table>
                          <TableHead>
                            <TableRow>
                              <TableCell>Agency</TableCell>
                              <TableCell>Emails Sent</TableCell>
                              <TableCell>Total Opens</TableCell>
                              <TableCell>Unique Opens</TableCell>
                              <TableCell>Total Clicks</TableCell>
                              <TableCell>Unique Clicks</TableCell>
                            </TableRow>
                          </TableHead>
                          <TableBody>
                            {tagAnalytics.agency_performance?.map((agency) => (
                              <TableRow key={agency.client_id}>
                                <TableCell>{agency.client_name}</TableCell>
                                <TableCell>{(agency.sent || 0).toLocaleString()}</TableCell>
                                <TableCell>{(agency.total_opens || 0).toLocaleString()} ({(agency.total_open_rate || 0).toFixed(1)}%)</TableCell>
                                <TableCell>{(agency.unique_opens || 0).toLocaleString()} ({(agency.unique_open_rate || 0).toFixed(1)}%)</TableCell>
                                <TableCell>{(agency.total_clicks || 0).toLocaleString()} ({(agency.total_click_rate || 0).toFixed(1)}%)</TableCell>
                                <TableCell>{(agency.unique_clicks || 0).toLocaleString()} ({(agency.unique_click_rate || 0).toFixed(1)}%)</TableCell>
                              </TableRow>
                            ))}
                          </TableBody>
                        </Table>
                      </TableContainer>
                    </Paper>
                  </>
                ) : (
                  <Alert severity="info">
                                          No analytics data available for the selected tag.
                  </Alert>
                )}
              </>
            )}
          </Box>
        </TabPanel>
      </Paper>

      {/* Tag Management Drawer */}
      <Drawer
        anchor="right"
        open={tagDrawerOpen}
        onClose={() => setTagDrawerOpen(false)}
        sx={{
          '& .MuiDrawer-paper': {
            width: 400,
            p: 3,
          },
        }}
      >
        <Box>
          <Box display="flex" justifyContent="space-between" alignItems="center" mb={3}>
            <Typography variant="h6">Add New Tag</Typography>
            <IconButton onClick={() => setTagDrawerOpen(false)}>
              <CloseIcon />
            </IconButton>
          </Box>

          <Paper sx={{ p: 3 }}>
            <Typography variant="h6" gutterBottom>
              Add New Tag
            </Typography>
            <Box display="flex" gap={1}>
              <TextField
                fullWidth
                size="small"
                placeholder="Tag name"
                value={newTagName}
                onChange={(e) => setNewTagName(e.target.value)}
                onKeyPress={(e) => e.key === 'Enter' && handleCreateTag()}
              />
              <Button
                variant="contained"
                onClick={handleCreateTag}
                disabled={!newTagName.trim()}
              >
                Add
              </Button>
            </Box>
          </Paper>
        </Box>
      </Drawer>

      {/* Edit Tag Dialog */}
      <Dialog open={!!editingTag} onClose={() => setEditingTag(null)}>
        <DialogTitle>Edit Tag</DialogTitle>
        <DialogContent>
          <TextField
            autoFocus
            margin="dense"
            label="Tag Name"
            fullWidth
            value={editTagName}
            onChange={(e) => setEditTagName(e.target.value)}
            onKeyPress={(e) => e.key === 'Enter' && handleUpdateTag()}
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setEditingTag(null)}>Cancel</Button>
          <Button onClick={handleUpdateTag} variant="contained">
            Update
          </Button>
        </DialogActions>
      </Dialog>

      {/* Delete Confirmation Dialog */}
      <Dialog open={deleteDialogOpen} onClose={() => setDeleteDialogOpen(false)}>
        <DialogTitle>Delete Tag</DialogTitle>
        <DialogContent>
          <Typography>
            Are you sure you want to delete "{tagToDelete?.name}"? This action cannot be undone.
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDeleteDialogOpen(false)}>Cancel</Button>
          <Button onClick={handleDeleteTag} color="error" variant="contained">
            Delete
          </Button>
        </DialogActions>
      </Dialog>

      {/* Snackbar */}
      <Snackbar
        open={snackbar.open}
        autoHideDuration={6000}
        onClose={() => setSnackbar({ ...snackbar, open: false })}
      >
        <Alert 
          onClose={() => setSnackbar({ ...snackbar, open: false })} 
          severity={snackbar.severity}
        >
          {snackbar.message}
        </Alert>
      </Snackbar>
    </PageContainer>
  );
};

export default Tags; 